import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import Tooltip from "@mui/material/Tooltip";
import { useToast } from "../../../../../Hooks/UseToast";
import { getSemana } from "../../../common/utils/semana";
import { fetchSucursales } from "../../../../RRHH2/features/legajos/services/empleados.api";
import { useConsolidado } from "../hooks/useConsolidado";
import {
  descargarConsolidadoTxt,
  descargarReporteBonfood,
  descargarReporteComidaAlPaso,
} from "../service/reportesService";
import ConsolidadoTable from "../components/ConsolidadoTable";

const semanaActual = getSemana(0);

const ReportesPage = () => {
  const [modo, setModo] = useState("rango");
  const [fecha, setFecha] = useState("");
  const [desde, setDesde] = useState(semanaActual.desde);
  const [hasta, setHasta] = useState(semanaActual.hasta);
  const [idSucursal, setIdSucursal] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [descargando, setDescargando] = useState(null);
  const [desdeBonfood, setDesdeBonfood] = useState(semanaActual.desde);
  const [descargandoBonfood, setDescargandoBonfood] = useState(false);
  const { showToast, Toast } = useToast();

  useEffect(() => {
    fetchSucursales()
      .then((res) => setSucursales(res || []))
      .catch(() => setSucursales([]));
  }, []);

  const filtros = useMemo(() => {
    const base = { id_sucursal: idSucursal || undefined };
    return modo === "fecha"
      ? { ...base, fecha: fecha || undefined }
      : { ...base, desde: desde || undefined, hasta: hasta || undefined };
  }, [modo, fecha, desde, hasta, idSucursal]);

  const { data, loading } = useConsolidado(filtros);

  const handleDescargarTxt = async () => {
    setDescargando("txt");
    try {
      await descargarConsolidadoTxt(filtros);
    } catch (err) {
      showToast(err.message || "Error al descargar el reporte.", "error");
    } finally {
      setDescargando(null);
    }
  };

  const handleDescargarComidaAlPaso = async () => {
    const base = modo === "fecha" ? fecha : desde;
    if (!base) {
      showToast("Elegí una fecha para saber qué semana exportar.", "warning");
      return;
    }
    setDescargando("excel");
    try {
      await descargarReporteComidaAlPaso(base);
    } catch (err) {
      showToast(
        err.message || "Error al descargar el reporte de Comida al Paso.",
        "error",
      );
    } finally {
      setDescargando(null);
    }
  };

  const handleDesdeBonfoodChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    if (moment(value, "YYYY-MM-DD").isoWeekday() !== 1) {
      showToast("Solo podés elegir un lunes: es el primer día de la semana a exportar.", "warning");
      return;
    }
    setDesdeBonfood(value);
  };

  const handleDescargarBonfood = async () => {
    if (!desdeBonfood) {
      showToast("Elegí el lunes de la semana a exportar.", "warning");
      return;
    }
    setDescargandoBonfood(true);
    try {
      await descargarReporteBonfood(desdeBonfood);
    } catch (err) {
      showToast(err.message || "Error al descargar el reporte de BonFood.", "error");
    } finally {
      setDescargandoBonfood(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={modo}
          onChange={(e, val) => val && setModo(val)}
        >
          <ToggleButton value="rango" sx={{ textTransform: "none" }}>
            Rango
          </ToggleButton>
          <ToggleButton value="fecha" sx={{ textTransform: "none" }}>
            Día puntual
          </ToggleButton>
        </ToggleButtonGroup>

        {modo === "fecha" ? (
          <TextField
            size="small"
            type="date"
            label="Fecha"
            InputLabelProps={{ shrink: true }}
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        ) : (
          <>
            <TextField
              size="small"
              type="date"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <Select
            displayEmpty
            value={idSucursal}
            onChange={(e) => setIdSucursal(e.target.value)}
          >
            <MenuItem value="">
              <em>Todas las sucursales</em>
            </MenuItem>
            {sucursales.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Exporta el Excel de Comida al Paso para toda la semana de la fecha elegida (Lunes a Viernes)">
          <span>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChartIcon />}
              disabled={!!descargando}
              onClick={handleDescargarComidaAlPaso}
              sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
            >
              Excel Comida al Paso
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          color="error"
          startIcon={<DescriptionIcon />}
          disabled={!data.length || !!descargando}
          onClick={handleDescargarTxt}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
        >
          TXT
        </Button>
      </Paper>

      <ConsolidadoTable data={data} loading={loading} />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 220 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
            Pedido semanal a BonFood
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Excel con las cantidades pedidas de esa semana, en el formato que espera BonFood.
          </Typography>
        </Box>

        <TextField
          size="small"
          type="date"
          label="Lunes de la semana"
          InputLabelProps={{ shrink: true }}
          value={desdeBonfood}
          onChange={handleDesdeBonfoodChange}
        />

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          color="warning"
          startIcon={<RestaurantIcon />}
          disabled={!desdeBonfood || descargandoBonfood}
          onClick={handleDescargarBonfood}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
        >
          Exportar BonFood
        </Button>
      </Paper>

      {Toast}
    </Box>
  );
};

export default ReportesPage;
